import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import homeHeaderRotateData from '../data/homeheaderrotate';

const placeholderImg = '/defaultimages/imagenotavailable.jpg';

const getSocialLinks = (item) => {
  const links = [
    { key: 'sm_insta', label: 'Instagram', icon: 'photo_camera', color: '#E1306C', borderColor: '#f5bfd1', bgColor: '#fff1f6' },
    { key: 'sm_facebook', label: 'Facebook', icon: 'facebook', color: '#1877F2', borderColor: '#bad7ff', bgColor: '#eff6ff' },
    { key: 'sm_whatsapp', label: 'WhatsApp', icon: 'chat', color: '#25D366', borderColor: '#b9efce', bgColor: '#effdf4' },
    { key: 'sm_youtube', label: 'YouTube', icon: 'smart_display', color: '#FF0000', borderColor: '#ffc2c2', bgColor: '#fff1f1' },
  ];

  return links
    .map(({ key, label, icon, color, borderColor, bgColor }) => ({
      label,
      icon,
      color,
      borderColor,
      bgColor,
      url: typeof item?.[key] === 'string' ? item[key].trim() : '',
    }))
    .filter((link) => Boolean(link.url));
};

const HomeHeaderRotateList = () => {
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [modalCard, setModalCard] = useState(null);
  const [selectedModalImage, setSelectedModalImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const selectedId =
    routeId ||
    searchParams.get('highlight') ||
    searchParams.get('id') ||
    searchParams.get('gid') ||
    searchParams.get('selectedId') ||
    searchParams.get('selectedid') ||
    searchParams.get('selected');

  const listings = useMemo(() => {
    return Array.isArray(homeHeaderRotateData) ? homeHeaderRotateData : [];
  }, []);

  const cards = useMemo(() => {
    return listings.map((item) => ({
      ...item,
      gid: item.id,
      category: item.businesstype || 'General',
      title: item.title || item.businessname || 'Untitled',
      nameoftheperson: item.businessname || '-',
      nameofthewisher: item.contactperson || '-',
      message: item.aboutdescription || '-',
      imageUrl: item.imagepath || placeholderImg,
    }));
  }, [listings]);

  useEffect(() => {
    if (!selectedId) return;

    const normalizedSelectedId = String(selectedId).trim();
    const highlighted = cards.find((item) => String(item.gid) === normalizedSelectedId);
    if (highlighted) {
      setSelectedCategory('all');
      setModalCard(highlighted);
      const photos = Array.isArray(highlighted.photos) ? highlighted.photos.filter(Boolean) : [];
      const initialImage = (photos.length > 0 ? photos[0] : (highlighted.imageUrl || highlighted.imagepath)) || placeholderImg;
      setSelectedModalImage(initialImage);
    }
  }, [selectedId, cards]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const updateViewport = () => setIsMobile(mediaQuery.matches);

    updateViewport();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateViewport);
      return () => mediaQuery.removeEventListener('change', updateViewport);
    }

    mediaQuery.addListener(updateViewport);
    return () => mediaQuery.removeListener(updateViewport);
  }, []);

  const categories = useMemo(() => {
    return ['all', ...new Set(cards.map((item) => item.category).filter(Boolean))];
  }, [cards]);

  const filteredListings = selectedCategory === 'all'
    ? cards
    : cards.filter((item) => item.category === selectedCategory);

  const sortedListings = [...filteredListings].sort((a, b) => (a.listingorder || 0) - (b.listingorder || 0));

  const openModal = (card) => {
    setModalCard(card);
    const photos = Array.isArray(card.photos) ? card.photos.filter(Boolean) : [];
    const initialImage = (photos.length > 0 ? photos[0] : (card.imageUrl || card.imagepath)) || placeholderImg;
    setSelectedModalImage(initialImage);
  };

  const closeModal = () => {
    setModalCard(null);
    setSelectedModalImage(null);
  };

  const socialLinks = modalCard ? getSocialLinks(modalCard) : [];

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            style={{
              padding: '0.6rem 1.2rem',
              border: selectedCategory === cat ? '2px solid #1976d2' : '1px solid #ccc',
              background: selectedCategory === cat ? '#e3f2fd' : '#fff',
              color: selectedCategory === cat ? '#1976d2' : '#333',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: selectedCategory === cat ? '600' : '400',
              textTransform: 'capitalize',
              transition: 'all 0.3s ease',
            }}
          >
            {cat.replace(/([A-Z])/g, ' $1').trim() || 'All'}
          </button>
        ))}
      </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        {sortedListings.length > 0 ? (
          sortedListings.map((card) => (
            <div
              key={card.gid || card.title}
              onClick={() => openModal(card)}
              style={{
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                background: '#fff',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{ height: '220px', overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src={card.imageUrl || card.imagepath || placeholderImg}
                  alt={card.title}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = placeholderImg;
                  }}
                />
              </div>

              <div style={{ padding: '1rem' }}>
                <span style={{ display: 'inline-block', padding: '0.3rem 0.8rem', background: '#e3f2fd', color: '#1976d2', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem', textTransform: 'capitalize' }}>
                  {card.category}
                </span>
                <h3 style={{ margin: '0.5rem 0', fontSize: '1.1rem', color: '#333', fontWeight: '600' }}>
                  {card.title}
                </h3>
                <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#666' }}>
                  {card.nameoftheperson}
                </p>
                <p style={{ margin: '0.5rem 0', fontSize: '0.85rem', color: '#888', fontStyle: 'italic' }}>
                  From: {card.nameofthewisher}
                </p>
              </div>
            </div>
          ))
        ) : (
          <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#999', padding: '2rem' }}>
            No listings found for this category.
          </p>
        )}
      </div>

      {modalCard && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: isMobile ? 'flex-start' : 'center',
            zIndex: 1000,
            padding: isMobile ? '0.5rem' : '1rem',
            overflowY: 'auto',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '8px',
              maxWidth: isMobile ? '100%' : '700px',
              width: '100%',
              maxHeight: isMobile ? 'calc(100vh - 1rem)' : '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              marginTop: isMobile ? '0' : '0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#f5f5f5',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: '#333',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ✕
            </button>

            <div style={{ padding: '1.5rem' }}>
              {(() => {
                const galleryImages = (Array.isArray(modalCard.photos) ? modalCard.photos : [])
                  .filter((img) => typeof img === 'string' && img.trim())
                  .map((img) => img.trim());
                if (galleryImages.length === 0) {
                  galleryImages.push((modalCard.imageUrl || modalCard.imagepath || placeholderImg));
                }

                return (
                  <>
                    <div style={{ marginBottom: '1rem', borderRadius: '8px', overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img
                        src={selectedModalImage || galleryImages[0] || placeholderImg}
                        alt={modalCard.title}
                        style={{ width: '100%', maxHeight: '500px', objectFit: 'contain' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = placeholderImg;
                        }}
                      />
                    </div>

                    {galleryImages.length > 1 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginBottom: '1.5rem' }}>
                        {galleryImages.map((imgUrl, idx) => (
                          <img
                            key={`${modalCard.gid || modalCard.id || modalCard.title}-thumb-${idx}`}
                            src={imgUrl || placeholderImg}
                            alt={`${modalCard.title} preview ${idx + 1}`}
                            style={{
                              width: '70px',
                              height: '48px',
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: (selectedModalImage || galleryImages[0]) === imgUrl ? '2px solid #1976d2' : '2px solid #e0e0e0',
                              cursor: 'pointer',
                              boxShadow: (selectedModalImage || galleryImages[0]) === imgUrl ? '0 0 6px rgba(25, 118, 210, 0.45)' : 'none',
                            }}
                            onClick={() => setSelectedModalImage(imgUrl || placeholderImg)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = placeholderImg;
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}

              <h2 style={{ fontSize: '1.5rem', color: '#333', marginBottom: '0.5rem' }}>
                {modalCard.title}
              </h2>

              <span style={{ display: 'inline-block', padding: '0.4rem 0.9rem', background: '#e3f2fd', color: '#1976d2', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600', marginBottom: '1rem', textTransform: 'capitalize' }}>
                {modalCard.category}
              </span>

              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f9f9f9', borderLeft: '4px solid #1976d2', borderRadius: '4px' }}>
                <p style={{ margin: '0.3rem 0', fontSize: '18px', color: '#666', fontWeight: 'bold' }}>
                  {modalCard.nameoftheperson}
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1rem', color: '#333', marginBottom: '0.5rem' }}>Message</h3>
                <p style={{ fontSize: '1rem', color: '#555', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {modalCard.message}
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem', padding: '0.8rem 1rem', background: '#f0f7ff', borderLeft: '3px solid #1976d2', borderRadius: '4px' }}>
                <p style={{ margin: 0, fontSize: '16px', color: '#666', fontWeight: 'bold' }}>
                  <strong>From:</strong> {modalCard.nameofthewisher}
                </p>
                {modalCard.contactnumber && (
                  <p style={{ margin: '6px 0 0 0', fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
                    Contact: {modalCard.contactnumber}
                  </p>
                )}
                {modalCard.address1 && (
                  <p style={{ margin: '6px 0 0 0', fontSize: '14px' }}>
                    Address: {modalCard.address1}
                  </p>
                )}
                {modalCard.website && modalCard.website !== '0' && (
                  <p style={{ margin: '6px 0 0 0', fontSize: '14px' }}>
                    <a href={modalCard.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                      {modalCard.website}
                    </a>
                  </p>
                )}

                {socialLinks.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#555', fontWeight: 600 }}>Social:</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {socialLinks.map((link) => (
                        <a
                          key={`${modalCard.gid || modalCard.id || modalCard.title}-${link.label}`}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={link.label}
                          aria-label={link.label}
                          style={{
                            textDecoration: 'none',
                            color: link.color,
                            width: '24px',
                            height: '24px',
                            border: `1px solid ${link.borderColor}`,
                            borderRadius: '50%',
                            background: link.bgColor,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <span className="material-icons" style={{ fontSize: '14px', lineHeight: 1 }}>
                            {link.icon}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <p style={{ fontSize: '0.85rem', color: '#999', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                Posted: {modalCard.dateposted || '-'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeHeaderRotateList;
